// Cash-friendly callout for per-trade marketing pages.
//
// Sits ABOVE the TapToPayCallout (different pitch, different visual)
// for trades flagged `cashFriendly: true` in for-trades.ts. The two
// callouts read as a pair:
//   - This one: "Manos doesn't take a cut on cash or off-platform
//     bank transfer." Anti-platform-fee differentiator.
//   - TapToPay: "Your phone IS the card reader." Modern feature.
//
// Visual is intentionally distinct from TapToPay -- this one shows a
// stylised payment-method comparison (cash + bank transfer + card
// stacked rows, with a "100% yours" badge on the first two), NOT a
// phone. Matches the Manos design tokens: rounded-md / rounded-lg
// radius, shadow-sm / shadow-md, soft accent wash background.
import * as React from "react";
import { Bank, CheckCircle, Money } from "@phosphor-icons/react";
import { FadeInOnScroll } from "@/components/ui/FadeInOnScroll";

export type CashCalloutAccent =
  | "amber"
  | "blue"
  | "green"
  | "rose"
  | "violet"
  | "teal"
  | "orange";

export interface CashCalloutProps {
  /** Trade-page accent so the section breathes in the same family. */
  accent?: CashCalloutAccent;
  /** Per-job ticket displayed on the comparison card. Defaults to
   *  £85.00; per-trade callers pass their own representative figure. */
  ticketDisplay?: string;
  /** Which side the illustration sits on. Defaults to "right" (copy
   *  on the left). */
  imageSide?: "left" | "right";
}

// Dark accent surfaces for the three-callout band (Cash, TapToPay,
// HouseProud). All three sections sit on dark backgrounds so the page
// reads dark/dark/dark for the bottom-of-page band rather than
// light/light/dark which felt off-balance against HouseProud's
// fixed dark-blue gradient.
const SECTION_BG: Record<CashCalloutAccent, string> = {
  amber: "bg-gradient-to-br from-amber-900 to-amber-800",
  rose: "bg-gradient-to-br from-rose-900 to-rose-800",
  violet: "bg-gradient-to-br from-violet-900 to-violet-800",
  teal: "bg-gradient-to-br from-teal-900 to-teal-800",
  green: "bg-gradient-to-br from-green-900 to-green-800",
  blue: "bg-gradient-to-br from-blue-900 to-blue-800",
  orange: "bg-gradient-to-br from-orange-900 to-orange-800",
};

// Heading-accent colour -- a lighter shade of the same family so it
// pops against the dark background.
const ACCENT_TEXT: Record<CashCalloutAccent, string> = {
  amber: "text-amber-200",
  rose: "text-rose-200",
  violet: "text-violet-200",
  teal: "text-teal-200",
  green: "text-green-200",
  blue: "text-blue-200",
  orange: "text-orange-200",
};

const ACCENT_TEXT_LIGHT: Record<CashCalloutAccent, string> = {
  amber: "text-amber-300",
  rose: "text-rose-300",
  violet: "text-violet-300",
  teal: "text-teal-300",
  green: "text-green-300",
  blue: "text-blue-300",
  orange: "text-orange-300",
};

const ACCENT_PILL: Record<CashCalloutAccent, string> = {
  amber: "bg-white/10 text-amber-100 border-white/20",
  rose: "bg-white/10 text-rose-100 border-white/20",
  violet: "bg-white/10 text-violet-100 border-white/20",
  teal: "bg-white/10 text-teal-100 border-white/20",
  green: "bg-white/10 text-green-100 border-white/20",
  blue: "bg-white/10 text-blue-100 border-white/20",
  orange: "bg-white/10 text-orange-100 border-white/20",
};

const BLUR_TINT: Record<CashCalloutAccent, string> = {
  amber: "bg-amber-400 opacity-20",
  rose: "bg-rose-400 opacity-20",
  violet: "bg-violet-400 opacity-20",
  teal: "bg-teal-400 opacity-20",
  green: "bg-green-400 opacity-20",
  blue: "bg-blue-400 opacity-20",
  orange: "bg-orange-400 opacity-20",
};

export function CashCallout({
  accent = "amber",
  ticketDisplay = "£85.00",
  imageSide = "right",
}: CashCalloutProps) {
  return (
    <FadeInOnScroll>
      <section
        className={`py-24 ${SECTION_BG[accent]} text-white relative overflow-hidden`}
      >
        {/* Background blur for depth -- soft accent glow on dark */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className={`absolute -top-32 left-1/3 w-80 h-80 rounded-full blur-3xl ${BLUR_TINT[accent]}`}
          />
          <div
            className={`absolute -bottom-24 -right-24 w-64 h-64 rounded-full blur-3xl ${BLUR_TINT[accent]}`}
          />
        </div>

        <div className="relative mx-auto max-w-6xl px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Copy column */}
            <div
              className={`space-y-6 ${
                imageSide === "left" ? "lg:order-2" : "lg:order-1"
              }`}
            >
              <div
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${ACCENT_PILL[accent]}`}
              >
                <Money
                  className={`h-4 w-4 ${ACCENT_TEXT_LIGHT[accent]}`}
                  weight="fill"
                />
                <span className="text-xs font-semibold uppercase tracking-wider">
                  Cash + bank transfer = 100% yours
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight text-white">
                Cash is still real money.
                <br />
                <span className={ACCENT_TEXT[accent]}>
                  Manos doesn&apos;t hide it.
                </span>
              </h2>
              <p className="text-base text-white/80 leading-relaxed max-w-md">
                Other platforms bury cash three menus deep -- they earn
                on card processing, so they nudge you onto card every
                time. Manos doesn&apos;t take a cut on cash or off-platform
                bank transfer. Subscription + a small share of card
                payments, that&apos;s the lot.
              </p>
              <ul className="space-y-3">
                {[
                  "Cash + direct bank transfers: 100% to you, no fees either side",
                  "Card payments work too, with Stripe's fee + a small Manos cut",
                  "One-tap mark-as-paid. Tax-ready exports treat all three the same",
                  "No nudging. Take the payment your customer wants to give",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-sm text-white/90"
                  >
                    <CheckCircle
                      className={`h-5 w-5 ${ACCENT_TEXT_LIGHT[accent]} shrink-0 mt-0.5`}
                      weight="fill"
                    />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Comparison illustration column */}
            <div
              className={`hidden lg:flex justify-center ${
                imageSide === "left" ? "lg:order-1" : "lg:order-2"
              }`}
            >
              <PaymentMethodComparison
                accent={accent}
                ticketDisplay={ticketDisplay}
              />
            </div>
          </div>
        </div>
      </section>
    </FadeInOnScroll>
  );
}

// Stylised receipt + three payment-method rows. Concrete, ASCII-only,
// matches the Manos design tokens (rounded-md / rounded-lg, shadow-md,
// stone-100 / stone-200 borders). Cash + bank transfer rows carry a
// "100% yours" emerald pill; card row shows the standard fee disclosure
// without burying it.
function PaymentMethodComparison({
  accent,
  ticketDisplay,
}: {
  accent: CashCalloutAccent;
  ticketDisplay: string;
}) {
  return (
    // The four inner cards (job-complete + three payment-method rows)
    // sit on the dark accent surface, so they need to stay LIGHT
    // regardless of the visitor's colour-mode preference. We lock to
    // bg-white + stone-900 text rather than the design-system bg-card
    // / text-foreground tokens (which flip in dark mode).
    <div className="w-full max-w-[420px] space-y-4">
      {/* Receipt header card -- the "job complete" pin */}
      <div className="rounded-lg border border-stone-200 bg-white text-stone-900 shadow-md p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">
              Job complete
            </p>
            <p className="text-2xl font-extrabold mt-1 text-stone-900">
              {ticketDisplay}
            </p>
          </div>
          <div className="inline-flex items-center justify-center h-10 w-10 rounded-md bg-emerald-100 text-emerald-600">
            <CheckCircle className="h-5 w-5" weight="fill" />
          </div>
        </div>
        <p className="text-xs text-stone-500">How was this paid?</p>
      </div>

      {/* Cash row -- 100% yours */}
      <PaymentRow
        icon={<Money className="h-5 w-5 text-emerald-600" weight="fill" />}
        label="Cash"
        sublabel="Pocketed, marked paid"
        keep="100%"
        keepBgClass="bg-emerald-100"
        keepTextClass="text-emerald-700"
      />

      {/* Bank transfer row -- 100% yours. Card is intentionally NOT
          rendered here -- the actual fee is variable (region, plan,
          processor) and the next section (TapToPayCallout) is where
          we talk about card. Keeping cash + transfer as the focused
          "100% yours" pair. */}
      <PaymentRow
        icon={<Bank className="h-5 w-5 text-emerald-600" weight="fill" />}
        label="Bank transfer"
        sublabel="Direct to your account"
        keep="100%"
        keepBgClass="bg-emerald-100"
        keepTextClass="text-emerald-700"
      />
    </div>
  );
}

function PaymentRow({
  icon,
  label,
  sublabel,
  keep,
  keepBgClass,
  keepTextClass,
}: {
  icon: React.ReactNode;
  label: string;
  sublabel: string;
  keep: string;
  keepBgClass: string;
  keepTextClass: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-stone-200 bg-white text-stone-900 p-4 shadow-sm">
      <div className="h-10 w-10 rounded-md bg-stone-100 flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-stone-900">{label}</p>
        <p className="text-xs text-stone-500">{sublabel}</p>
      </div>
      <span
        className={`shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${keepBgClass} ${keepTextClass}`}
      >
        {keep} yours
      </span>
    </div>
  );
}
